import { useEffect, useRef } from 'react'

export interface UseModalA11yOptions {
  /** Whether the overlay is currently open. */
  open: boolean
  /** Called when the user requests close via Escape or any hook-managed gesture. */
  onClose: () => void
  /**
   * Listen for the Escape key and call onClose.
   * Default: true
   */
  escapeToClose?: boolean
  /**
   * Lock body scroll (`overflow: hidden`) while open and restore on close.
   * Default: true
   */
  scrollLock?: boolean
  /**
   * Trap Tab / Shift+Tab focus inside the container, move focus in on open, and
   * restore the previously-focused element on close.
   * Requires the returned ref to be attached to the container element.
   * Default: false
   */
  focusTrap?: boolean
  /**
   * Focusable element selector used by the focus-trap.
   * Override only if the default set is too broad or too narrow for a particular
   * overlay (unlikely in practice).
   */
  focusableSelector?: string
}

const DEFAULT_FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * Shared accessibility primitives for overlay components (Modal, BottomSheet,
 * Drawer).  Returns a `ref` to attach to the container element (required when
 * `focusTrap` is enabled; harmless when not).
 *
 * Behaviour controlled per-option:
 *   escapeToClose  — keydown Escape → onClose()                  (default on)
 *   scrollLock     — body overflow: hidden while open             (default on)
 *   focusTrap      — Tab/Shift+Tab cycle + focus-in + restore     (default off)
 */
export function useModalA11y({
  open,
  onClose,
  escapeToClose = true,
  scrollLock = true,
  focusTrap = false,
  focusableSelector = DEFAULT_FOCUSABLE,
}: UseModalA11yOptions): React.RefObject<HTMLElement | null> {
  const containerRef = useRef<HTMLElement | null>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)

  // Escape key + body scroll lock (combined because they share the same
  // open guard and cleanup pattern).
  useEffect(() => {
    if (!open) return

    const listeners: Array<() => void> = []

    if (escapeToClose) {
      const onKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape') onClose()
      }
      document.addEventListener('keydown', onKey)
      listeners.push(() => document.removeEventListener('keydown', onKey))
    }

    let prevOverflow = ''
    if (scrollLock) {
      prevOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      listeners.push(() => {
        document.body.style.overflow = prevOverflow
      })
    }

    return () => {
      listeners.forEach((fn) => fn())
    }
    // onClose identity is intentionally excluded: changes to it mid-open must
    // not restart the scroll-lock (which would flash the overflow back briefly).
    // Components pass stable callbacks (useCallback or inline stable refs) so
    // this is safe.  Re-evaluate if a consumer ever passes an unstable onClose.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, escapeToClose, scrollLock])

  // Focus management: move focus into the container on open; restore on close.
  useEffect(() => {
    if (!focusTrap) return

    if (open) {
      previousFocusRef.current = document.activeElement as HTMLElement
      // Defer one animation frame so the panel is in the DOM before focusing.
      const id = requestAnimationFrame(() => {
        containerRef.current?.focus()
      })
      return () => cancelAnimationFrame(id)
    } else {
      previousFocusRef.current?.focus()
      previousFocusRef.current = null
    }
  }, [open, focusTrap])

  // Tab / Shift+Tab focus trap.
  useEffect(() => {
    if (!focusTrap || !open) return

    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return
      const panel = containerRef.current
      if (!panel) return
      const focusable = panel.querySelectorAll<HTMLElement>(focusableSelector)
      if (!focusable.length) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault()
          last.focus()
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault()
          first.focus()
        }
      }
    }

    document.addEventListener('keydown', handleTab)
    return () => document.removeEventListener('keydown', handleTab)
  }, [open, focusTrap, focusableSelector])

  return containerRef
}
